import type { Facility, SimpleSelectable } from '../../types/db.ts';
import type { TRIFacilityDataType } from '../../types/form-r.ts';
import type { Warning } from '../../types/tri.ts';
import { triStates } from '../../data/tri-states.ts';

export function formRFacilityDetails(facility: SimpleSelectable<Facility>, warnings: Warning[]): TRIFacilityDataType {
  // We don't validate most other fields because (1) many of them are optional and we can't programmatically tell that
  // they should be there, and (2) many of them are required to be in the XML export but TRI-MEweb always prefers its
  // own stored values for them (address, etc.), so there's no point validating something that will be thrown away.
  if (!/^[A-Z0-9]{15}$/.test(facility.triID)) {
    warnings.push({
      warningID: 'TW1124',
      message: `Facility TRI ID "${facility.triID}" does not appear to be valid (expected 15-digit alphanumeric, all uppercase).`,
      type: 'facility',
    });
  }

  const isExportingMailingAddress =
    !!facility.mailingAddress.trim() &&
    !!facility.mailingCity.trim() &&
    !!facility.mailingState.trim() &&
    !!facility.mailingZip.trim();

  const locationState = facility.state.toUpperCase();
  const mailingState = facility.mailingState.toUpperCase();

  if (!triStates.includes(locationState)) {
    warnings.push({
      warningID: 'TW1129',
      type: 'facility',
      message: `The facility does not appear to have a valid state listed: "${locationState}"`,
    });
  }

  if (isExportingMailingAddress && !triStates.includes(mailingState)) {
    warnings.push({
      warningID: 'TW1130',
      type: 'facility',
      message: `The facility does not appear to have a valid state listed for its mailing address: "${mailingState}"`,
    });
  }

  return {
    FacilityAccessDetails: null, // Presumably access to TRI-MEweb implies this, and therefore it is not required.
    FacilityIdentifier: [{ value: facility.triID, attrs: { FacilitySiteIdentifierContext: 'TRIME-WEB' } }],
    FacilitySiteName: facility.properName,

    FacilityNAICS: facility.naicsCodes.map((code, i) => ({
      NAICSCode: code,
      NAICSPrimaryIndicator: i === 0 ? 'Primary' : 'Secondary',
    })),

    // SIC code is no longer needed, as https://www.epa.gov/toxics-release-inventory-tri-program/tri-covered-industry-sectors says:
    // "However, the Office of Management and Budget replaced the SIC code system with the NAICS code system developed by
    // the U.S. Census Bureau, and the TRI Program adopted this system in 2006 (71 FR 32464)."
    FacilitySIC: [],

    NPDESIdentificationNumber: facility.npdesPermitNumbers,
    RCRAIdentificationNumber: facility.rcraIdNumbers,
    UICIdentificationNumber: facility.uicIdNumbers,
    FacilityDunBradstreetCode: facility.dunBradstreetCodes.map((code) => code.padStart(9, '0')),

    ParentCompanyNameNAIndicator: !facility.parentCompanyName,
    ParentCompanyNameNotStandard: null, // "Indicates that the parent company name was not the EPA provided standardized name." it's not clear how we'd determine this, so leaving as null.
    ParentCompanyNameText: facility.parentCompanyName || 'NA',
    ParentDunBradstreetCode: facility.parentCompanyDunBradstreetCode
      ? facility.parentCompanyDunBradstreetCode.padStart(9, '0')
      : 'NA',

    ForeignParentCompanyNameNAIndicator: !facility.foreignParentCompanyName,
    ForeignParentCompanyNameNotStandard: null, // "Indicates that the foreign parent company name was not the EPA provided standardized name." it's not clear how we'd determine this, so leaving as null.
    ForeignParentCompanyNameText: facility.foreignParentCompanyName || 'NA',
    ForeignParentDunBradstreetCode: facility.foreignParentCompanyDunBradstreetCode
      ? facility.foreignParentCompanyDunBradstreetCode.padStart(9, '0')
      : 'NA',

    GeographicLocationDescription: null, // Facilities can provide lat/long. We're going to assume address will be enough.
    LocationAddress: {
      LocationAddressText: facility.address,
      LocalityName: facility.city,
      StateIdentity: {
        StateCode: null, // It's not clear if this is an abbreviation like "AZ" or something else, leaving as null for now.
        StateCodeListIdentifier: null, // I'm unable to find any examples of what this would be.
        StateName: locationState,
      },
      AddressPostalCode: {
        value: facility.zip,
        attrs: {}, // There is no description of the meaning of the AddressPostalCodeContext attribute, so leaving it out.
      },
      CountyIdentity: {
        CountyCode: null, // It's unclear what this would be (do counties even have standard codes/abbreviations?)
        CountyCodeListIdentifier: null, // I'm unable to find any examples of what this would be.
        CountyName: facility.county,
      },
      CountryIdentity: null, // According to the XSL sheet, this is only needed for non-US addresses, which are exceedingly unlikely.

      LocationDescriptionText: null, // Facilities can provide navigation instructions to their location. We're going to assume address will be enough.
      SupplementalLocationText: null, // "Additional information about a place, including a building name with its secondary unit and number, an industrial park name, an installation name or descriptive text where no formal address is available" We're going to assume address will be enough.

      // If we get any tribal controlled facilities on tribal lands we'll need to add these fields:
      TribalIdentity: null,
      TribalLandIndicator: null,
      TribalLandName: null,
    },

    MailingFacilitySiteName: isExportingMailingAddress ? facility.properName : 'NA',
    MailingAddress: {
      MailingAddressText: isExportingMailingAddress ? facility.mailingAddress : null,
      SupplementalAddressText: null, // "text that provides additional information to facilitate the delivery of a mail piece, including building name, secondary units, and mail stop or local box numbers not serviced by the U.S. Postal Service". I doubt we'll need this, leaving it as null.
      MailingAddressCityName: isExportingMailingAddress ? facility.mailingCity : null,
      ProvinceNameText: null, // If the facility receives mail in a non-US country, we'll need to provide this, but I find that unlikely.
      StateIdentity: {
        StateCode: null, // It's not clear if this is an abbreviation like "AZ" or something else, leaving as null for now.
        StateCodeListIdentifier: null, // I'm unable to find any examples of what this would be.
        StateName: isExportingMailingAddress ? mailingState : null,
      },
      AddressPostalCode: isExportingMailingAddress
        ? {
            value: facility.mailingZip,
            attrs: {}, // There is no description of the meaning of the AddressPostalCodeContext attribute, so leaving it out.
          }
        : null,
      CountryIdentity: null, // According to the XSL sheet, this is only needed for non-US addresses, which are exceedingly unlikely.
    },
  };
}
